import { useEffect, useRef, useState } from "react";
import { Message, Json } from "../model";

type Timer = ReturnType<typeof setTimeout>;

export type Api = {
	scheduleSync(cause: string, jsonContent: Json, svgContent: string): void;
	undo(): void;
	redo(): void;
};

export function useApi({
	loadFromJson,
	onHydrateChange,
	onHistoryChange,
}: {
	loadFromJson(json: Json): Promise<void>;
	onHydrateChange(isHydrating: boolean): void;
	onHistoryChange(h: { can_undo: boolean; can_redo: boolean }): void;
}): Api {
	let syncTimer = useRef<Timer>(null);
	let isHydrating = false;
	let isSynced = false;

	const url = new URL(window.location.href);
	const search = new URLSearchParams(url.search);
	const userId = search.get("user_id")!;

	const protocol = url.protocol === "https:" ? "wss:" : "ws:";
	const socketUrl = `${protocol}//${url.hostname}${url.port ? `:${url.port}` : ""}/draw?${search}`;
	const socketRef = useRef<WebSocket | null>(null);
	const unmounting = useRef(false);
	const reconnectTimerRef = useRef<Timer>(null);

	const pingTimerRef = useRef<Timer>(null);
	let lastPongAt = 0;
	let awaitingPong = false;
	const PING_INTERVAL = 10 * 1000;

	function notifyBoardUpdated(svg: string) {
		// @ts-expect-error
		window.webkit?.messageHandlers?.snapshot?.postMessage({
			type: "boardUpdated",
			svg,
		});
	}

	function startPing(socket: WebSocket | null) {
		clearTimeout(pingTimerRef.current!);
		lastPongAt = Date.now();
		awaitingPong = false;

		pingTimerRef.current = setInterval(() => {
			if (!socket) {
				console.log(`❌ no socket in setInterval`);
				return;
			}

			if (awaitingPong) {
				console.log(`❌ still awaiting pong in setInterval, reconnect`);
				socket.close(1000, "healthcheck failed, reconnect");
				return;
			}

			send({
				type: "ping",
				userId,
			});
			awaitingPong = true;
		}, PING_INTERVAL);
	}

	function setHydrating(hydrating: boolean) {
		isHydrating = hydrating;
		onHydrateChange(hydrating);
	}

	useEffect(() => {
		unmounting.current = false;

		function onSync() {
			isSynced = true;
		}

		function onUnsync() {
			isSynced = false;
		}

		function connect() {
			if (unmounting.current) return;

			console.log(`connect() - create new socket`);
			socketRef.current = new WebSocket(socketUrl);

			socketRef.current.onopen = () => {
				console.log("connected, syncing...");
				if (!isSynced) {
					socketRef.current?.send(
						JSON.stringify({
							type: "sync",
							userId,
						}),
					);
				}
			};

			socketRef.current.onmessage = async (event) => {
				const msg = JSON.parse(event.data) as Message;
				console.log(`💌`, msg.type);

				switch (msg.type) {
					case "sync_res":
						console.log(`✅✅✅✅ sync_res`, msg);
						onSync();
						if (msg.payload2) {
							if (msg.payload2.json) {
								setHydrating(true);
								await loadFromJson(JSON.parse(msg.payload2.json as any));
							}
							onHistoryChange({
								can_undo: msg.payload2.can_undo,
								can_redo: msg.payload2.can_redo,
							});
							setTimeout(() => {
								setHydrating(false);
								startPing(socketRef.current);
								// Notify board update after sync to ensure we get a snpashot on every app open
								notifyBoardUpdated(msg.payload2.svg);
							}, 1);
							return;
						}

						setHydrating(false);
						break;
					case "pong":
						console.log(`✅ received pong`);
						lastPongAt = Date.now();
						awaitingPong = false;
						break;
				}
			};

			socketRef.current.onclose = (ev: CloseEvent) => {
				console.log(`❌ socket.onclose()`, ev.code, ev.reason);
				onUnsync();
				isHydrating = true;
				onHydrateChange(true);
				clearTimeout(reconnectTimerRef.current!);
				clearTimeout(pingTimerRef.current!);

				reconnectTimerRef.current = setTimeout(() => {
					connect()!;
				}, 1000);
			};
		}

		connect();

		return function destroySocket() {
			console.log(`🚨 destroySocket()`);
			unmounting.current = true;
			if (reconnectTimerRef.current) {
				clearTimeout(reconnectTimerRef.current);
			}
			socketRef.current?.close(1000, "useApi unmount");
			socketRef.current = null;
		};
	}, []);

	function send(msg: Message) {
		if (!socketRef.current) {
			console.log(`❌ send called on missing socket`);
			return;
		}

		console.log(`socket.send`, socketRef.current.readyState, msg);
		socketRef.current.send(JSON.stringify(msg));
	}

	function syncBoard(jsonContent: Json, svgContent: string) {
		if (!isSynced) {
			console.log(`♻️ syncBoard aborted bc not synced`);
			return;
		}

		send({
			type: "draw",
			payload: {
				json: jsonContent,
				svg: svgContent,
			},
			userId,
		});
		notifyBoardUpdated(svgContent);
	}

	return {
		scheduleSync: (cause: string, jsonContent: Json, svgContent: string) => {
			if (isHydrating) {
				return;
			}
			console.log(`scheduleSync`, cause);
			if (syncTimer.current) {
				clearTimeout(syncTimer.current);
			}
			syncTimer.current = setTimeout(
				() => syncBoard(jsonContent, svgContent),
				300,
			);
		},
		redo: () => {
			setHydrating(true);
			send({
				type: "redo",
				userId,
			});
		},
		undo: () => {
			setHydrating(true);
			send({
				type: "undo",
				userId,
			});
		},
	};
}
